import {Component, OnInit, OnDestroy, ChangeDetectorRef, Input, ViewChild} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router, RouterOutlet} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TableModule} from 'primeng/table';
import {ButtonModule} from 'primeng/button';
import {TagModule} from 'primeng/tag';
import {SelectModule} from 'primeng/select';
import {InputTextModule} from 'primeng/inputtext';
import {DialogModule} from 'primeng/dialog';
import {TooltipModule} from 'primeng/tooltip';
import {DatePickerModule} from 'primeng/datepicker';
import {MenuModule} from 'primeng/menu';
import {MenuItem} from 'primeng/api';
import {PaginatorModule} from 'primeng/paginator';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {RequestsService, BidFilter} from '@services/requests.service';
import {forkJoin, Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {BidType, FieldFilter, Region} from "@6grain/masofa-monolith-api-client/dictionaries";
import {Crop} from "@app/api/alert";
import {RegionService} from "@6grain/masofa-monolith-api-client/dictionaries/api/region.service";
import {CropService} from "@6grain/masofa-monolith-api-client/dictionaries/api/crop.service";
import {NotReadyApiService} from "@app/api/not-ready-api-service";
import {ListPageWrapper} from "@app/wrappers/pages/list-page-wrapper/list-page-wrapper";
import {BackButton} from "@components/ui/back-button/back-button";
import {Bid} from "@6grain/masofa-monolith-api-client/crop-monitoring";
import {BidBaseGetQuery} from "@6grain/masofa-monolith-api-client/crop-monitoring/model/bidBaseGetQuery";
import {RequestStatus} from "@app/pages/crop-monitoring/components/request-status/request-status";
import {Filters} from "@components/views/filters/filters";
import {IMsfFormBuilder} from "@components/form-builder/form-builder";
import {ServerTranslatePipe} from "@app/utils/pipes/server-translate-pipe";
import {DateFormatLangPipe} from "@app/utils/pipes/date-format-lang-pipe";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {User, UserService} from '@6grain/masofa-monolith-api-client/identity';
import {ExportButton} from '@components/button/export-button/export-button';
import {buildFilterQuery, replaceFilter, userNames} from '@app/utils/_msf';
import {DynamicDataTableComponent} from '@components/dynamic-data-table/dynamic-data-table.component';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {SortType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import {RequestParams} from '@components/select/resource-component';
import {RequestResultModalComponent} from '@app/pages/crop-monitoring/components/request-result-modal/request-result-modal.component';
import {IconPathPipe} from '@app/utils/pipes/icon-path-pipe';
import {FilterOperator} from '@app/utils/filter-operator';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {RegionDisplay} from '@components/region-display/region-display';
import {BID_STATUS_USES} from '@shared/data/bid-status-uses-data';
import {GisBidsComponent} from '@app/pages/crop-monitoring/pages/gis-bids/gis-bids';
import {FieldPassportLinkComponent} from '@components/field-passport-link/field-passport-link.component';
import {FieldService} from '@6grain/masofa-monolith-api-client/crop-monitoring';

type RequestFieldRow = Pick<Bid, 'fieldId'> & {
  field?: {
    id?: string | null;
    name?: string | null;
  } | null;
  fieldName?: string | null;
};


@Component({
  selector: 'app-requests-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    TagModule,
    SelectModule,
    InputTextModule,
    DialogModule,
    TooltipModule,
    DatePickerModule,
    MenuModule,
    PaginatorModule,
    ProgressSpinnerModule,
    ListPageWrapper,
    BackButton,
    RequestStatus,
    Filters,
    ServerTranslatePipe,
    DateFormatLangPipe,
    ExportButton,
    DynamicDataTableComponent,
    RouterOutlet,
    RequestResultModalComponent,
    IconPathPipe,
    PrimaryButton,
    RegionDisplay,
    GisBidsComponent,
    FieldPassportLinkComponent,
  ],
  providers: [DatePipe, DateFormatLangPipe],
  templateUrl: './requests-page.html',
  styleUrl: './requests-page.scss',
})
export class RequestsPageComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  @ViewChild(RequestResultModalComponent) requestResultModal!: RequestResultModalComponent;

  isSmallScreen: boolean = false;

  /** List vs map on full requests page (widget stays list-only). */
  requestsViewTab: 'board' | 'map' = 'board';

  protected readonly userNames = userNames;

  isFilterModalOpen: boolean = false;
  requestFilters: IMsfFormBuilder[] = []

  requests: Bid[] = [];

  loading = false;
  exportLoading = false;
  filteredRequestsCount = 0;

  first = 0;
  rows = 10;
  totalRecords = 0;
  rowsPerPageOptions = [
    {label: '7', value: 7},
    {label: '10', value: 10},
    {label: '25', value: 25},
    {label: '50', value: 50}
  ];

  // Filter options
  filters: BidFilter = {};
  regionOptions: (Region & { name: string })[] = [];
  cropOptions: (Crop & { name: string })[] = [];
  bidTypeOptions: (BidType & { name: string })[] = [];
  foremanOptions: any[] = [];
  usersOptions: any[] = [];

  public fieldWorkOptions: User[] = [];
  // Menu items for create request dropdown
  createRequestMenuItems: MenuItem[] = [];


  constructor(
    private cdr: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
    public router: Router,
    public requestsService: RequestsService,
    public translate: TranslateService,
    public regionService: RegionService,
    public cropService: CropService,
    public fieldService: FieldService,
    public bidService: BidService,
    public notReadyApiService: NotReadyApiService,
    public userService: UserService,
    private regionManagerService:RegionManagerService
  ) {
    this.updateMenuItems();
    this.bidService.cropMonitoringBidGetTotalCountPost()
  }

  @Input({required: true}) defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    sortBy: 'number',
    sort: SortType.NUMBER_1,
    filters: []
  }

  @Input({required: true}) storageKey: string = 'requests_page';

  @Input() isWidget: boolean = false;


  @Input() toggleFilter: boolean | undefined = undefined;

  @Input() flexRowSizes = {
    left: '9',
    right: '3'
  }

  @Input() public filtersParams: FieldFilter[] = [];

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.Tablet]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.isSmallScreen = result.matches;
      if (!this.isWidget) {
        this.flexRowSizes = this.isSmallScreen ? {left: '12', right: '0'} : {left: '9', right: '3'};
      }
    });

    const clonedBidStatuses = JSON.parse(JSON.stringify(BID_STATUS_USES));

    this.userService.identityUserGetByQueryPost({}).subscribe(users => {
      this.usersOptions = users.map(u => ({
        value: u.id,
        label: (u.lastName && u.firstName)
          ? `${u.lastName} ${u.firstName}`
          : u.userName
      }));
    });

    this.requestFilters = [
      {
        name: 'bidState',
        title: 'Status',
        type: 'select',
        placeholder: 'Select status',
        getSelectOptions: () => {
          return clonedBidStatuses
        },
      },
      {
        name: 'deadlineDate',
        title: 'Deadline',
        type: 'date',
        placeholder: 'Select deadline',
        options:
          {
            date: {
              selectionMode: 'range',
            }
          }
      },
      {
        name: 'number',
        title: 'Request number',
        type: 'input-number',
        placeholder: 'Enter request number'
      },
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagerService,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',
          }
        },
        placeholder: 'Select region',
      },
      {
        name: 'fieldId',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.fieldService,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none'
          }
        }
      },
      {
        name: 'cropId',
        title: 'Crop',
        type: 'select',
        placeholder: 'Select crop',
        selectService: this.cropService,
        selectServiceMethod: 'dictionariesCropGetByQueryPost',
      },
      {
        name: 'workerId',
        title: 'Field worker',
        type: 'select',
        placeholder: 'Select field worker',
        selectOptions: null,
        getSelectOptions: () => {
          return this.fieldWorkOptions
        },
        options: {
          select: {
            optionLabel: 'label',
            optionValue: 'value',
            translateBy: 'none'
          }
        }
      },
      {
        name: 'foremanId',
        title: 'Foreman',
        type: 'select',
        selectOptions: null,
        getSelectOptions: () => {
          return this.foremanOptions
        },
        options: {
          select: {
            optionLabel: 'label',
            optionValue: 'value',
            translateBy: 'none'
          }
        }
      },
      {
        name: 'createUser',
        title: 'Author',
        type: 'select',
        placeholder: "Select author",
        selectService: this.userService,
        selectServiceMethod: 'identityUserGetByQueryPost',
        options: {
          select: {
            optionLabel: 'userName',
            optionValue: 'id',
            translateBy: 'none'
          }
        }
      },
      {
        name: 'QwenAnomalyCount',
        title: 'Anomalies',
        type: 'checkbox',
      },
    ]

    // this.loadRequests();
    this.loadMetadata();
    this.loadFilteredCount();

    // Subscribe to language changes
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateMenuItems();
      // Trigger change detection to update date formats
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateMenuItems() {
    this.createRequestMenuItems = [
      {
        label: this.translate.instant('Ground survey'),
        command: () => this.createGroundSurveyRequest()
      },
      {
        label: this.translate.instant('UAV'),
        command: () => this.createUAVRequest()
      }
    ];
  }

  private loadMetadata() {
    forkJoin({
      crops: this.requestsService.getCrops(),
      bidTypes: this.requestsService.getBidTypes(),
      foremen: this.notReadyApiService.loadForemen(),
      fieldWorker: this.notReadyApiService.loadFieldWorkers()
    }).subscribe({
      next: ({ crops, bidTypes, foremen, fieldWorker}) => {


        this.cropOptions = crops.items

        this.bidTypeOptions = [
          ...bidTypes.items?.map((t: any) => ({label: t.name, value: t.id})) || []
        ];

        this.foremanOptions = foremen;

        this.fieldWorkOptions = fieldWorker;


        this.cdr.markForCheck();
      },
      error: () => {
        this.cdr.markForCheck();
      }
    });
  }

  toUTCMidnight(date: Date): string {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
      .toISOString();
  }

  async applyFilters() {
    this.filtersParams = await this.buildFiltersParams();
    this.loadFilteredCount();
    this.toggleFilter = !this.toggleFilter;
  }

  private async buildFiltersParams(): Promise<FieldFilter[]> {

    const params: BidFilter = {...this.filters} as any;

    // if (this.filters.deadlineDate) {
    //   if (this.filters.deadlineDate[0]) {
    //     params.startDate = this.toUTCMidnight(new Date(this.filters.deadlineDate[0]));
    //   }
    //   if (this.filters.deadlineDate[1]) {
    //     params.endDate = this.toUTCMidnight(new Date(this.filters.deadlineDate[1]));
    //   }
    //   delete params.deadlineDate;
    // }


    // Handle anomaliesCount separately with greater than operator
    const hasStartDateFilter = this.filters.deadlineDate && this.filters.deadlineDate[0];
    const hasEndDateFilter = this.filters.deadlineDate && this.filters.deadlineDate[1];
    const hasAnomaliesFilter = params.QwenAnomalyCount;

    const computedFilters = buildFilterQuery(params as Record<string, any>) as FieldFilter[];
    await this.regionManagerService.includeChildInRequest(params, computedFilters as any);


    // Add anomaliesCount filter with greater than operator
    if (hasAnomaliesFilter) {
      replaceFilter('QwenAnomalyCount',
        {
          filterField: 'QwenAnomalyCount',
          filterOperator: FilterOperator.GreaterThanOrEqual,
          filterValue: 1
        }, computedFilters)
    }

    if (hasStartDateFilter) {
      const dates = this.filters.deadlineDate as string [];
      replaceFilter('deadlineDate', {
        filterField: 'startDate',
        filterOperator: FilterOperator.GreaterThanOrEqual,
        filterValue: this.toUTCMidnight(new Date(dates[0]))
      }, computedFilters)

      if (hasEndDateFilter) {
        computedFilters.push(
          {
            filterField: 'endDate',
            filterOperator: FilterOperator.LessThanOrEqual,
            filterValue: this.toUTCMidnight(new Date(dates[1]))
          }
        )
      }

    }
    return computedFilters;
  }

  clearFilters() {
    this.filters = {}
    this.filtersParams = [];
    this.loadFilteredCount();
    this.toggleFilter = !this.toggleFilter;
  }

  private loadFilteredCount(): void {
    this.bidService
      .cropMonitoringBidGetTotalCountPost({filters: this.filtersParams})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count: number) => {
          this.filteredRequestsCount = Number(count) || 0;
          this.cdr.markForCheck();
        },
        error: () => {
          this.filteredRequestsCount = 0;
          this.cdr.markForCheck();
        }
      });
  }

  createGroundSurveyRequest() {
    this.router.navigate(['crop-monitoring', 'requests', 'add']);
  }

  createUAVRequest() {
    this.router.navigate(['crop-monitoring', 'requests', 'add'], {queryParams: {type: 'limiting'}});
  }

  editRequest(request: Bid) {
    this.router.navigate(['crop-monitoring', 'requests', 'edit', request.id]);
  }

  openFilterModal(): void {
    this.isFilterModalOpen = true;
  }


  async export() {
    const query: BidBaseGetQuery = {
      filters: await this.buildFiltersParams()
    };
    this.exportLoading = true;

    this.bidService.cropMonitoringBidExportFromCSVPost(
      query,
      'body',
      false,
      { httpHeaderAccept: 'application/octet-stream' as any }
    ).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.exportLoading = false)
    ).subscribe({
      next: (result: any) => {
        if (result instanceof Blob) {
          this.downloadCsvBlob(result);
          return;
        }

        if (typeof result === 'string' && result.length > 0) {
          this.downloadCsvContent(result);
          return;
        }

        if (Array.isArray(result) && result.length > 0) {
          const rows = [Object.keys(result[0]).join(','), ...result.map((row: any) => Object.values(row).join(','))];
          this.downloadCsvContent(rows.join('\n'));
        }
      }
    });
  }

  private downloadCsvBlob(blob: Blob) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.buildCsvFileName();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private downloadCsvContent(result: string) {
    let rows = result.split('\n').map(r => r.trim()).filter(r => r);

    const headers = rows[0].split(',');
    const uuidIndex = headers.findIndex(h =>
      h.toLowerCase().includes('id') || h.toLowerCase().includes('uuid')
    );

    if (uuidIndex !== -1) {
      rows = rows.map(r => {
        const cols = r.split(',');
        cols.splice(uuidIndex, 1);
        return cols.join(',');
      });
    }

    const translatedHeaders = rows[0]
      .split(',')
      .map(h => {
        const key = h?.trim();
        if (!key) {
          return '';
        }

        const translated = this.translate.instant(key);
        return translated || key;
      });
    rows[0] = translatedHeaders.join(',');

    const csvContent = rows.join('\n');

    const blob = new Blob(["\uFEFF" + csvContent], {type: 'text/csv;charset=utf-8;'});
    this.downloadCsvBlob(blob);
  }

  private buildCsvFileName(): string {
    const filePrefix = this.translate.instant('Requests');

    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const formattedDate = `${day}${month}${year}`;

    return `${filePrefix}_${formattedDate}.csv`;
  }

  openRequestResultModal(fileResultId: string) {
    this.requestResultModal?.open(fileResultId);
  }

  resultReady(row: any): boolean {
    return row.fileResultId && row.fileResultId !== "00000000-0000-0000-0000-000000000000"
  }

  fieldId(row: RequestFieldRow): string | null {
    const id = row?.field?.id ?? row?.fieldId;
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  fieldName(row: RequestFieldRow): string | null {
    const name = row?.field?.name ?? row?.fieldName;
    return name != null && String(name).trim() ? String(name).trim() : null;
  }

}
